import { HStack, VStack } from '@chakra-ui/react';
import { Button, IconButton } from 'Atoms';
import { CelsiaSpinner, SearchInput } from 'Molecules';
import { Typography } from 'Tokens';
import { HistoryIcon, RefreshIcon } from 'Tokens/Icons/Function';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { HistoryItem } from 'Molecules/HistoryItem';
import { AuditLog } from './History.hooks';
import { useDebounce } from 'utils/hooks';
import { TFunction } from 'utils/translation';
import { filterOptions, HistorySideBarFilter } from 'Molecules/HistorySidebarFilter';

export enum HistoryContext {
  all,
  taxonomy,
  esrs,
  pai,
}

export const HistorySidebarHeader = ({ t }: { t: TFunction }) => {
  return (
    <HStack pt="8px" w="100%" justify="space-between">
      <HStack>
        <HistoryIcon color="text.muted" />
        <Typography variant="bodyStrong" color="text.muted">
          {t('audit:common.history')}
        </Typography>
      </HStack>
    </HStack>
  );
};

export const HistorySideBarFiltering = ({
  title,
  searchValue,
  setSearchValue,
  refetchAuditLogs,
  filterOptions,
  selected,
  setSelected,
  t,
}: {
  title: string;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  refetchAuditLogs: () => void;
  filterOptions?: filterOptions;
  selected?: { [x: string]: string[] };
  setSelected?: (val: { [x: string]: string[] }) => void;
  t: TFunction;
}) => {
  return (
    <VStack alignItems="flex-start" pt="8px" w="100%">
      <Typography variant="h2">{title}</Typography>
      <HStack width="100%" spacing="8px">
        <SearchInput
          search={searchValue}
          setSearch={(input) => setSearchValue(input)}
          placeholder={t('files:searchPlaceholder')}
          width="100%"
          size="md"
        />
        {filterOptions && (
          <HistorySideBarFilter
            filterOptions={filterOptions}
            setSelected={setSelected}
            selected={selected}
          />
        )}
        <IconButton
          aria-label="Refresh"
          size="md"
          variant="secondary"
          icon={<RefreshIcon color="inherit" />}
          onClick={refetchAuditLogs}
        />
      </HStack>
    </VStack>
  );
};

export const HistorySidebar = ({
  auditLogs,
  loading,
  searchValue,
  loadMore,
  hasMoreData,
  refetchLoading,
  targetCount,
  incrementTargetCount,
  noPadding,
  selected,
}: {
  auditLogs: AuditLog[];
  loading: boolean;
  searchValue: string;
  loadMore: () => void;
  hasMoreData: boolean;
  refetchLoading: boolean;
  targetCount: number;
  incrementTargetCount: () => void;
  noPadding?: boolean;
  selected?: { [x: string]: string[] };
}) => {
  const [loadedLogs, setLoadedLogs] = useState<AuditLog[]>([]);
  const [stagedLogs, setStagedLogs] = useState<AuditLog[]>([]);
  const [pendingFetch, setPendingFetch] = useState(false);

  const debouncedSearch = useDebounce(searchValue, 500);

  const getValidLogs = (logs: AuditLog[], existingIds: Set<string>) =>
    logs.filter(
      (log) =>
        !log.isSkipped &&
        !existingIds.has(log.id) &&
        !log.dataChanges?.every((dataChange) => !dataChange.oldData && !dataChange.newData)
    );

  useEffect(() => {
    const spaceLeft = targetCount - loadedLogs.length;
    const hasEnoughStagedLogs = stagedLogs.length >= spaceLeft && loadedLogs.length < targetCount;

    if (
      hasEnoughStagedLogs ||
      (!hasMoreData && stagedLogs.length > 0 && loadedLogs.length < targetCount)
    ) {
      setLoadedLogs((prev) => [...prev, ...stagedLogs.slice(0, spaceLeft)]);
      setStagedLogs(stagedLogs.slice(spaceLeft));
      setPendingFetch(false);
    } else if (loadedLogs.length < targetCount && hasMoreData && !loading) {
      loadMore();
    }
  }, [stagedLogs, targetCount, loading, hasMoreData]);

  useEffect(() => {
    if (!loading && auditLogs.length > 0) {
      const existingIds = new Set([...loadedLogs, ...stagedLogs].map((l) => l.id));
      const valid = getValidLogs(auditLogs, existingIds);

      setStagedLogs((prev) => [...prev, ...valid]);
    }
  }, [auditLogs, loading]);

  useEffect(() => {
    if (refetchLoading) {
      setLoadedLogs([]);
      setStagedLogs([]);
    }
  }, [refetchLoading]);

  const handleLoadMore = () => {
    incrementTargetCount();
    setPendingFetch(true);
  };

  const filteredLogs = useMemo(() => {
    if (!selected || !Object.keys(selected).length)
      return loadedLogs.filter((log) =>
        JSON.stringify(log).toLowerCase().includes(debouncedSearch.toLowerCase())
      );

    return loadedLogs.filter((log) => {
      const conditions: [key: string, path: any][] = [
        ['Standard', log.metaInfo?.standardName],
        ['Disclosure requirement', log.metaInfo?.drName],
        ['Business unit', log.metaInfo?.reportingUnit],
        ['Metric type', log.metaInfo?.metricType],
        ['Datapoint owner', log.user?.displayName],
      ];
      return (
        conditions.every(([key, value]) => {
          const selectedValues = selected[key];
          return !value || !selectedValues || selectedValues.some((val) => value.includes(val));
        }) && JSON.stringify(log).toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    });
  }, [selected, debouncedSearch, loadedLogs]);

  const hasMoreToLoad = hasMoreData || stagedLogs.length > 0;

  if (loadedLogs.length === 0 && (loading || hasMoreData || refetchLoading))
    return <CelsiaSpinner />;

  return (
    <VStack
      width="100%"
      bg="bg.default"
      spacing="4px"
      borderBottom="1px  solid"
      borderBottomColor="border.decorative"
      borderRadius="8px"
    >
      {filteredLogs.map((log) => (
        <HistoryItem key={log.id} auditLog={log} noPadding={noPadding} />
      ))}

      <VStack spacing={2} p={2}>
        <Button
          onClick={handleLoadMore}
          isDisabled={pendingFetch || !hasMoreToLoad}
          isLoading={pendingFetch}
        >
          {pendingFetch ? 'Loading' : hasMoreToLoad ? 'Load more' : 'No more logs'}
        </Button>
      </VStack>
    </VStack>
  );
};
